import { avatarHue, initialsOf, memberHues } from '../../core/avatar';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { photoPickProblem } from '../../ui/photo-pick';
import { PhotoCropData, PhotoCropDialogComponent } from '../photo-crop-dialog/photo-crop-dialog.component';
import { ChatSheetService } from '../../chat-sheet.service';
import { ChatService } from '../../chat.service';
import { ChatUiService } from '../../chat-ui.service';
import { ConversationSummary, HubRequestError, MemberView, NotifyLevel } from '../../core/hub-types';

/**
 * A conversation's details and members (tasks/messaging-hub-architecture.md §6.2, O11, O16).
 *
 *   everyone        members (with who is online), mute, leave (not a 1:1)
 *   managers        channel owners/admins and practice admins: rename, describe, add and remove members, archive
 *   practice admin  delete a channel (soft; hidden from everyone, kept until erasure)
 *   group message   any member adds people
 * The hub enforces all of this; the buttons only hide what would be refused.
 */
@Component({
  selector: 'app-conversation-info-dialog',
  templateUrl: './conversation-info-dialog.component.html',
  styleUrls: ['./conversation-info-dialog.component.scss'],
})
export class ConversationInfoDialogComponent implements OnInit {
  members: MemberView[] = [];
  candidates: { principalId: string; name: string }[] = [];
  loading = true;
  busy = false;
  error = '';
  adding = false;
  /** The task dialog's "Assigned to" pattern: a multiple select of practice members not already here. */
  toAddIds: string[] = [];
  name = '';
  description = '';
  /** The name and description open for editing (the pencil beside the name). */
  editing = false;
  readonly notifyOptions: { level: NotifyLevel; label: string }[] = [
    { level: 'all', label: 'Every message' },
    { level: 'mentions', label: 'Only when I’m mentioned' },
    { level: 'none', label: 'Nothing' },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: { conversationId: string; isOrgAdmin: boolean },
    private readonly ref: MatDialogRef<ConversationInfoDialogComponent>,
    private readonly chat: ChatService,
    private readonly ui: ChatUiService,
    private readonly sheets: ChatSheetService,
    private readonly dialog: MatDialog,
  ) {}

  get c(): ConversationSummary | undefined {
    return this.chat.store.snapshot.conversations[this.data.conversationId];
  }

  get me(): string | undefined {
    return this.chat.store.snapshot.me?.principalId;
  }

  get canManage(): boolean {
    const c = this.c;
    if (!c) return false;
    if (c.type === 'channel') return this.data.isOrgAdmin || c.myRole === 'owner' || c.myRole === 'admin';
    return c.type === 'group_dm' && c.access === 'participant';
  }

  get muted(): boolean {
    return !!this.c && this.chat.store.isMuted(this.c);
  }

  /** Muting is offered to anyone in the conversation. */
  get canMute(): boolean {
    return this.c?.access === 'participant';
  }


  /** A person's photo in a list of people, or null when they have none (their initials are shown instead). */
  facePhoto(p: { principalId: string; avatar?: string }): string | null {
    return this.chat.store.avatarUrl(p.principalId, p.avatar ?? null);
  }

  faceInitials(p: { name: string }): string {
    return initialsOf(p.name);
  }

  faceHue(p: { principalId: string }): number {
    return memberHues(this.members.map((m) => m.principalId)).get(p.principalId) ?? avatarHue(p.principalId);
  }

  async ngOnInit(): Promise<void> {
    this.name = this.c?.name ?? '';
    this.description = this.c?.description ?? '';
    await this.loadMembers();
  }

  private async loadMembers(): Promise<void> {
    this.loading = true;
    try {
      this.members = await this.chat.store.members(this.data.conversationId);
    } catch (e) {
      this.fail(e);
    } finally {
      this.loading = false;
    }
  }

  async startAdding(): Promise<void> {
    const c = this.c;
    if (!c) return;
    this.adding = true;
    const inIt = new Set(this.members.map((m) => m.principalId));
    try {
      await this.chat.syncRoster().catch(() => undefined);
      this.candidates = (await this.chat.store.directory(c.orgId)).filter((p) => !inIt.has(p.principalId));
    } catch (e) {
      this.fail(e);
    }
  }

  cancelAdding(): void {
    this.adding = false;
    this.toAddIds = [];
  }

  async addSelected(): Promise<void> {
    await this.run(async () => {
      await this.chat.store.addMembers(this.data.conversationId, [...this.toAddIds]);
      this.toAddIds = [];
      this.adding = false;
      await this.loadMembers();
    });
  }

  async removeMember(m: MemberView): Promise<void> {
    if (!(await this.sheets.confirm({ title: 'Remove member', message: `Remove ${m.name} from this conversation?`, confirmText: 'Remove', danger: true }))) return;
    await this.run(async () => {
      await this.chat.store.removeMember(this.data.conversationId, m.principalId);
      await this.loadMembers();
    });
  }

  startEdit(): void {
    this.name = this.c?.name ?? '';
    this.description = this.c?.description ?? '';
    this.editing = true;
  }

  cancelEdit(): void {
    this.editing = false;
  }

  async saveDetails(): Promise<void> {
    await this.run(() => this.chat.store.rename(this.data.conversationId, this.name.trim(), this.description.trim() || null));
    if (!this.error) this.editing = false;
  }

  // ── the hero at the top ──────────────────────────────────────────────────────────

  /** The group's photo, or the other person's in a one-to-one. */
  get heroPhoto(): string | null {
    return this.c ? this.chat.store.photoUrlOf(this.c) : null;
  }

  get heroIconName(): string {
    switch (this.c?.type) {
      case 'channel': return this.c.visibility === 'private' ? 'lock' : 'groups';
      case 'patient': return 'personal_injury';
      case 'referral': return 'swap_horiz';
      case 'patient_direct': return 'phone_iphone';
      case 'network': return 'medical_services';
      case 'support': return 'support_agent';
      case 'group_dm': return 'group';
      default: return 'person';
    }
  }

  get heroTitle(): string {
    return this.c ? this.chat.store.titleOf(this.c) : '';
  }

  /** What kind of conversation, and how many are in it. */
  get heroSubtitle(): string {
    const c = this.c;
    if (!c) return '';
    const count = this.loading ? '' : ` · ${this.members.length} ${this.members.length === 1 ? 'member' : 'members'}`;
    switch (c.type) {
      case 'channel': return `${c.visibility === 'private' ? 'Private group: invitation only' : 'Public group: anyone in the practice can join'}${count}`;
      case 'patient': return `Patient chat: everyone in the practice can read it${count}`;
      case 'referral': return `Referral chat${c.referral?.counterpart ? ` with ${c.referral.counterpart}` : ''}${count}`;
      case 'patient_direct': return 'The patient, from the patient app';
      case 'network': return 'A colleague outside your practice';
      case 'support': return 'MedicAI Support';
      case 'group_dm': return `Group message${count}`;
      default: return 'Direct message';
    }
  }

  /** Whether the actions card has anything in it. */
  get hasActions(): boolean {
    const c = this.c;
    if (!c) return false;
    return (c.type === 'channel' && (this.canManage || this.data.isOrgAdmin)) || (c.access === 'participant' && c.type !== 'direct');
  }

  async setNotifyLevel(level: NotifyLevel): Promise<void> {
    await this.run(() => this.chat.store.setNotifyLevel(this.data.conversationId, level));
  }

  async toggleMute(): Promise<void> {
    // Muting silences the badge and notifications (M4), for a week; unmute any time.
    await this.run(() => this.chat.store.mute(this.data.conversationId, this.muted ? null : new Date(Date.now() + 7 * 86_400_000)));
  }

  async archive(archived: boolean): Promise<void> {
    await this.run(() => this.chat.store.archive(this.data.conversationId, archived));
  }

  async leave(): Promise<void> {
    if (!(await this.sheets.confirm({ title: 'Leave conversation', message: 'You will stop receiving its messages.', confirmText: 'Leave', danger: true }))) return;
    await this.run(async () => {
      await this.chat.store.leave(this.data.conversationId);
      this.ref.close();
      this.ui.showList();
    });
  }

  async deleteChannel(): Promise<void> {
    const ok = await this.sheets.confirm({
      title: 'Delete group',
      message: 'The group disappears for everyone. Messages already copied into patient records stay there.',
      confirmText: 'Delete group',
      danger: true,
    });
    if (!ok) return;
    await this.run(async () => {
      await this.chat.store.deleteChannel(this.data.conversationId);
      this.ref.close();
      this.ui.showList();
    });
  }

  // ── the group's photo ────────────────────────────────────────────────────────────

  /** The group's photo, or null (its lock or people icon shows instead). */
  get groupPhoto(): string | null {
    return this.c?.type === 'channel' ? this.chat.store.photoUrlOf(this.c) : null;
  }

  /** Pick, crop to the square everyone sees in the circle, upload. Only for those who may rename the group. */
  async pickPhoto(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.busy) return;
    const problem = photoPickProblem(file);
    if (problem) {
      this.error = problem;
      return;
    }
    const cropped = await firstValueFrom(this.dialog.open<PhotoCropDialogComponent, PhotoCropData, Blob | null>(
      PhotoCropDialogComponent, { width: '380px', maxWidth: '95vw', data: { file, fileName: file.name, title: 'Choose the group photo' } },
    ).afterClosed());
    if (!cropped) return;
    await this.run(async () => {
      await this.chat.store.setConversationPhoto(this.data.conversationId, cropped, file.name);
      await this.chat.store.refreshConversation(this.data.conversationId);
    });
  }

  async removePhoto(): Promise<void> {
    await this.run(async () => {
      await this.chat.store.clearConversationPhoto(this.data.conversationId);
      await this.chat.store.refreshConversation(this.data.conversationId);
    });
  }

  private async run(work: () => Promise<unknown>): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      await work();
    } catch (e) {
      this.fail(e);
    } finally {
      this.busy = false;
    }
  }

  private fail(e: unknown): void {
    const code = e instanceof HubRequestError ? e.code : '';
    this.error = code === 'FORBIDDEN' ? 'You cannot do that in this conversation.' : code === 'READ_ONLY' ? 'This conversation is read-only.' : 'Something went wrong.';
  }
}
